import ExtendableError from '../lib/ExtendableError';
import { ApiError } from './API';

export class OrderError extends ExtendableError {
}

export class InvalidOrderStateError extends OrderError {
    name = "InvalidOrderStateError";

    constructor () {
        super(`Invalid order state`);
    }
}

export class TooManyTicketsError extends OrderError {
    message = 'Cannot add more tickets to basket';
    name = 'TooManyTicketsError';
}

export class NotEnoughAvailabilityError extends OrderError {
    message = 'No availability for requested product.';
    name = 'NotEnoughAvailabilityError';
}

export class DiscountCodeError extends OrderError {
    message = 'Discount code is using in different ticket';
    name = 'DiscountCodeError';
}

export class InteropError extends OrderError {
    message = 'This fare is not available for this train operator';
    name = 'InteropError';
}

// A 2xx reply with no JSON `result` (an empty 204, or text/HTML from a proxy or
// gateway) is parsed by the API client into a string, so it reaches the service as
// a "successful" response with nothing in it. A reply that claims to be JSON but is
// empty makes the client's own parse throw instead (`parseError`). The received
// shape is the only clue to which case it was, so it goes in the message.
export class EmptyOrderResponseError extends OrderError {
    name = 'EmptyOrderResponseError';

    constructor (body, parseError) {
        let received;
        if (parseError) {
            received = `unparseable JSON (${parseError.message})`;
        } else if (typeof body === 'string') {
            received = `${body.length ? 'non-JSON text' : 'empty body'} (${body.length} chars)`;
        } else {
            received = body === null || body === undefined ? String(body) : 'JSON without a result';
        }
        super(`Order response has no result: received ${received}`);
    }
}

export const OrderErrorCodes = {
    INVALID_ORDER_STATE: 6002,
    TOO_MANY_TICKETS: 7001,
    NO_SEATS_AVAILABLE: 12000
};

// The split-trip passenger-count-mismatch rejection carries a generic `code: 0`
// (indistinguishable from other errors - see `e.code &&` in errors/API.js, which
// already treats 0 as "no code"), so it can only be matched by its message, which
// otherwise embeds the order id and so can't be compared with exact equality.
export const SPLIT_TRIP_PASSENGER_COUNT_MISMATCH_MESSAGE =
    'New trips passenger count does not match the tickets being amended for order';

const PROMO_INVALID_DISCOUNT_STATE = 'InvalidPromotionalDiscountState';

const hasErrorMessage = (error, message) => {
    const errors = error && error.body && error.body.errors;
    if (!Array.isArray(errors)) {
        return false;
    }
    return errors.some((err) => err && err.message === message);
};

export function throwCorrectOrderError (error) {
    if (error instanceof ApiError) {
        switch (true) {
            case error.hasCode(OrderErrorCodes.INVALID_ORDER_STATE):
                throw new InvalidOrderStateError();
            case hasErrorMessage(error, PROMO_INVALID_DISCOUNT_STATE):
                throw new DiscountCodeError();
            case error.hasCode(OrderErrorCodes.TOO_MANY_TICKETS):
                throw new TooManyTicketsError();
            case error.hasCode(OrderErrorCodes.NO_SEATS_AVAILABLE):
                throw new NotEnoughAvailabilityError();
        }
    }

    throw error;
}
